// File: src/components/Vendors/VendorFormModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/axios"; // ⬅️ use shared axios instance
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

/* ---------- small, fast UI bits ---------- */
const Tab = React.memo(function Tab({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3.5 py-2 rounded-lg text-sm border transition whitespace-nowrap
        ${selected ? "bg-blue-600 border-blue-500 text-white" : "bg-gray-800 border-white/10 text-gray-200 hover:bg-gray-700"}`}
    >
      {children}
    </button>
  );
});

const Field = React.memo(function Field({
  name,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  left,
  right,
  inputProps = {},
  className = "",
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label htmlFor={name} className="text-xs text-gray-300">
        {label}{required ? " *" : ""}
      </Label>
      <div className="flex items-stretch gap-2">
        {left}
        <Input
          id={name}
          name={name}
          type={type}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete="on"
          className="bg-gray-800 text-white border-white/10 focus-visible:ring-2 focus-visible:ring-blue-600"
          {...inputProps}
        />
        {right}
      </div>
    </div>
  );
});

/* ---------- data & component ---------- */
const SECTION_KEYS = ["basics", "payment", "banking", "invoicing"];
const CURRENCIES = ["GBP", "EUR", "USD", "AED", "AUD", "CAD"];

const emptyForm = {
  vendor_name: "",
  contact_name: "",
  contact_person: "",
  email: "",
  invoice_email: "",
  phone: "",
  address: "",
  delivery_address: "",
  payment_terms: "",
  delivery_schedule: "",
  unit_name: "",
  vat_number: "",
  account_reference_number: "",
  account_number: "",
  account_number_vendor: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_name: "",
  bank_address: "",
  sort_code: "",
  iban: "",
  swift_code: "",
  currency: "GBP",
  default_currency: "GBP",
  wise_recipient_id: "",
  active: true,
};

export default function VendorFormModal({ open, onClose, onSuccess, initialData }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("basics");

  useEffect(() => {
    if (initialData) {
      const currency = initialData.currency || "GBP";
      const default_currency = initialData.default_currency || currency || "GBP";
      const wise_recipient_id =
        initialData.wise_recipient_id != null ? String(initialData.wise_recipient_id) : "";
      setForm({ ...emptyForm, ...initialData, currency, default_currency, wise_recipient_id });
    } else {
      setForm(emptyForm);
      setSection("basics");
    }
  }, [initialData, open]);

  const isEdit = useMemo(() => Boolean(initialData?.vendor_id), [initialData]);

  // stable change handler factory → avoids re-renders while typing
  const handleChange = useCallback(
    (name) =>
      (e) => {
        const next = e?.target ? e.target.value : e;
        setForm((prev) => (prev[name] === next ? prev : { ...prev, [name]: next }));
      },
    []
  );

  const handleKeyDown = useCallback((e) => {
    const isCmdOrCtrl = e.metaKey || e.ctrlKey;
    if (isCmdOrCtrl && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.vendor_name.trim()) {
      setSection("basics");
      document.getElementById("vendor_name")?.focus();
      return;
    }
    const payload = {
      ...form,
      currency: String(form.currency || "GBP").toUpperCase(),
      default_currency: String(form.default_currency || form.currency || "GBP").toUpperCase(),
      wise_recipient_id: (form.wise_recipient_id || "").trim().replace(/[^\d]/g, "") || null,
    };

    setLoading(true);
    try {
      if (isEdit) {
        // ⬇️ no /solo-mdsh-api prefix here
        await api.put(`/vendors/${initialData.vendor_id}`, payload);
      } else {
        await api.post(`/vendors`, payload);
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Failed to save vendor:", err);
      alert(err?.response?.data?.error || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const next = () =>
    setSection((prev) => SECTION_KEYS[Math.min(SECTION_KEYS.length - 1, SECTION_KEYS.indexOf(prev) + 1)]);
  const prev = () =>
    setSection((prev) => SECTION_KEYS[Math.max(0, SECTION_KEYS.indexOf(prev) - 1)]);

  const onDialogChange = (v) => {
    if (!v || !loading) onClose?.(v);
  };

  return (
    <Dialog open={open} onOpenChange={onDialogChange}>
      {/* Wider but safe; header/footer sticky; body is the only scroller */}
      <DialogContent
        className="bg-gray-900 text-white w-[min(1280px,calc(100vw-2rem))] sm:max-w-[1280px]
                   p-0 h-[min(94vh,calc(100dvh-2rem))] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-white/10 bg-gray-900/95 backdrop-blur px-6 py-4">
          <DialogHeader className="p-0">
            <DialogTitle className="text-xl">{isEdit ? "✏️ Edit Vendor" : "➕ Add Vendor"}</DialogTitle>
          </DialogHeader>
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pr-1">
            <Tab selected={section === "basics"} onClick={() => setSection("basics")}>Basics</Tab>
            <Tab selected={section === "payment"} onClick={() => setSection("payment")}>Payment &amp; Delivery</Tab>
            <Tab selected={section === "banking"} onClick={() => setSection("banking")}>Banking</Tab>
            <Tab selected={section === "invoicing"} onClick={() => setSection("invoicing")}>Invoicing &amp; IDs</Tab>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* BASICS — 3 columns on desktop (stable) */}
          {section === "basics" && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <Field name="vendor_name" label="Vendor Name" required value={form.vendor_name}
                     onChange={handleChange("vendor_name")}
                     placeholder="e.g., FreshHarvest Ltd."
                     inputProps={{ id: "vendor_name", autoFocus: !isEdit, autoComplete: "organization" }} />
              <Field name="contact_name" label="Contact Name" value={form.contact_name}
                     onChange={handleChange("contact_name")} placeholder="e.g., Sarah Malik"
                     inputProps={{ autoComplete: "name" }} />
              <Field name="contact_person" label="Contact Person" value={form.contact_person}
                     onChange={handleChange("contact_person")} placeholder="If different"
                     inputProps={{ autoComplete: "name" }} />
              <Field name="phone" label="Phone" value={form.phone}
                     onChange={handleChange("phone")} placeholder="+44 20 1234 5678"
                     inputProps={{ inputMode: "tel", autoComplete: "tel" }} />
              <Field name="email" type="email" label="Email" value={form.email}
                     onChange={handleChange("email")} placeholder="hello@vendor.com"
                     inputProps={{ autoComplete: "email" }} />
              <Field name="invoice_email" type="email" label="Invoice Email" value={form.invoice_email}
                     onChange={handleChange("invoice_email")} placeholder="accounts@vendor.com"
                     inputProps={{ autoComplete: "email" }} />

              <Field name="address" label="Registered Address" value={form.address}
                     onChange={handleChange("address")} placeholder="Street, City, Postcode"
                     className="xl:col-span-3" inputProps={{ autoComplete: "street-address" }} />
              <Field name="delivery_address" label="Delivery Address" value={form.delivery_address}
                     onChange={handleChange("delivery_address")} placeholder="Where to receive goods"
                     className="xl:col-span-3" inputProps={{ autoComplete: "street-address" }} />

              {/* Status (no wrap) */}
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-300">Status</Label>
                <div className="flex items-center gap-3 flex-nowrap">
                  <button
                    type="button"
                    onClick={() => setForm((p) => (p.active ? p : { ...p, active: true }))}
                    className={`px-4 py-2 rounded-lg border text-sm min-w-[110px]
                      ${form.active ? "bg-emerald-600 border-emerald-500" : "bg-gray-800 border-white/10"}`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => (!p.active ? p : { ...p, active: false }))}
                    className={`px-4 py-2 rounded-lg border text-sm min-w-[110px]
                      ${!form.active ? "bg-rose-600 border-rose-500" : "bg-gray-800 border-white/10"}`}
                  >
                    Inactive
                  </button>
                </div>
              </div>

              {/* Currency */}
              <div>
                <Label className="text-xs text-gray-300">Currency</Label>
                <select
                  className="mt-1.5 bg-gray-800 text-white border border-white/10 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={form.currency || "GBP"}
                  onChange={(e) => handleChange("currency")(e)}
                >
                  {CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
                <div className="text-[11px] text-gray-400 mt-1">Transaction currency used with this vendor.</div>
              </div>

              {/* Default Currency */}
              <div>
                <Label className="text-xs text-gray-300">Default Currency</Label>
                <div className="mt-1.5 flex items-stretch gap-2">
                  <select
                    className="bg-gray-800 text-white border border-white/10 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-600"
                    value={form.default_currency || form.currency || "GBP"}
                    onChange={(e) => handleChange("default_currency")(e)}
                  >
                    {CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                  <Button
                    type="button"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => setForm((p) => ({ ...p, default_currency: p.currency || "GBP" }))}
                  >
                    Match
                  </Button>
                </div>
                <div className="text-[11px] text-gray-400 mt-1">
                  Fallback currency (used when a doc doesn’t specify).
                </div>
              </div>
            </div>
          )}

          {/* PAYMENT */}
          {section === "payment" && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <Field name="payment_terms" label="Payment Terms" value={form.payment_terms} onChange={handleChange("payment_terms")} placeholder="e.g., Net 30" />
              <Field name="delivery_schedule" label="Delivery Schedule" value={form.delivery_schedule} onChange={handleChange("delivery_schedule")} placeholder="e.g., Mon/Wed/Fri" />
              <Field name="unit_name" label="Unit Name" value={form.unit_name} onChange={handleChange("unit_name")} placeholder="e.g., Cases, Crates, Pallets" />
            </div>
          )}

          {/* BANKING */}
          {section === "banking" && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <Field name="bank_account_name" label="Bank Account Name" value={form.bank_account_name} onChange={handleChange("bank_account_name")} />
              <Field name="bank_account_number" label="Bank Account Number" value={form.bank_account_number} onChange={handleChange("bank_account_number")} inputProps={{ inputMode: "numeric" }} />
              <Field name="account_number" label="Account Number (Internal)" value={form.account_number} onChange={handleChange("account_number")} />
              <Field name="account_number_vendor" label="Vendor Account Number" value={form.account_number_vendor} onChange={handleChange("account_number_vendor")} />
              <Field name="bank_name" label="Bank Name" value={form.bank_name} onChange={handleChange("bank_name")} />
              <Field name="bank_address" label="Bank Address" value={form.bank_address} onChange={handleChange("bank_address")} className="xl:col-span-3" />
              <Field name="sort_code" label="Sort Code" value={form.sort_code} onChange={handleChange("sort_code")} placeholder="e.g., 12-34-56" inputProps={{ inputMode: "numeric" }} />
              <Field name="iban" label="IBAN" value={form.iban} onChange={handleChange("iban")} />
              <Field name="swift_code" label="SWIFT / BIC" value={form.swift_code} onChange={handleChange("swift_code")} />
              <Field
                name="wise_recipient_id"
                label="Wise Recipient ID"
                value={form.wise_recipient_id}
                onChange={handleChange("wise_recipient_id")}
                placeholder="Digits only"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                right={
                  form.wise_recipient_id ? (
                    <Button type="button" variant="ghost" className="text-xs" onClick={() => setForm((p) => ({ ...p, wise_recipient_id: "" }))}>
                      Clear
                    </Button>
                  ) : null
                }
              />
            </div>
          )}

          {/* INVOICING */}
          {section === "invoicing" && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <Field name="vat_number" label="VAT Number" value={form.vat_number} onChange={handleChange("vat_number")} />
              <Field name="account_reference_number" label="Account Reference Number" value={form.account_reference_number} onChange={handleChange("account_reference_number")} />
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t border-white/10 bg-gray-900/95 backdrop-blur px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button type="button" variant="ghost" className="text-gray-300 min-w-[120px]" onClick={prev}>← Prev</Button>
              <Button type="button" variant="ghost" className="text-gray-300 min-w-[120px]" onClick={next}>Next →</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading} className="text-gray-300 min-w-[140px]">Cancel</Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700 min-w-[220px]">
                {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
                {isEdit ? "Update Vendor" : "Create Vendor"}
                <span className="ml-2 text-xs opacity-75">(⌘/Ctrl + Enter)</span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
